import { useTranslation } from 'react-i18next';
import BestPriceButton from '@/components/common/BestPriceButton';
import type { BestPriceAction } from '@/components/common/BestPriceLabel';
import IdText from '@/components/common/IdText';
import { formatMoney } from '@/lib/format';
import classes from './BestPriceResultsTable.module.css';

/** Строка выдачи: контрагент, пункт, качество, предоплата, цена и ссылка на сделку. */
export interface BestPriceResultRow {
  readonly key: string;
  readonly party: string;
  readonly inn?: string;
  readonly place?: string;
  readonly quality?: string;
  readonly prepayment?: string;
  readonly price: number;
  /** Ссылка на встречное предложение; нет — ячейка действия пустая (агент смотрит справочно, выдача устарела). */
  readonly to?: string;
}

interface BestPriceResultsTableProps {
  /** Строки отсортированы сервером по выгоде: первая — лучшая цена. */
  readonly rows: readonly BestPriceResultRow[];
  readonly action: BestPriceAction;
  /** Колонка действия: у агента её нет. Строки без ссылки (устаревшая выдача) оставляют ячейку пустой — таблица не прыгает. */
  readonly withAction: boolean;
  readonly labels: {
    readonly party: string;
    readonly place: string;
    readonly quality: string;
    readonly prepayment: string;
    readonly price: string;
  };
}

// Выдача поиска лучшей цены таблицей «Бюллетеня» (общая для продавца и покупателя). Лучшая цена — первая строка:
// число весом 600, пометка «лучшая цена» и залитая кнопка; звёзд, заливки и рамок нет. На телефоне строка — блок
// с подписями значений, пустые значения не выводятся.
export default function BestPriceResultsTable({ rows, action, withAction, labels }: BestPriceResultsTableProps) {
  const { t } = useTranslation();

  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th scope="col">{labels.party}</th>
          <th scope="col">{labels.place}</th>
          <th scope="col">{labels.quality}</th>
          <th scope="col">{labels.prepayment}</th>
          <th scope="col" className={classes.num}>{labels.price}</th>
          {withAction ? (
            <th scope="col" className={classes.action}>
              <span className={classes.hidden}>{t('app.bestPrice.results.action')}</span>
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => {
          const best = index === 0;
          return (
            <tr key={row.key}>
              <th scope="row" className={classes.party}>
                {row.party}
                {row.inn ? (
                  <span className={classes.inn}>
                    {t('auth.fields.inn')} <IdText>{row.inn}</IdText>
                  </span>
                ) : null}
              </th>
              <td className={row.place ? undefined : classes.blank} data-label={labels.place}>
                {row.place}
              </td>
              <td className={row.quality ? classes.nowrap : classes.blank} data-label={labels.quality}>
                {row.quality}
              </td>
              <td className={row.prepayment ? classes.nowrap : classes.blank} data-label={labels.prepayment}>
                {row.prepayment}
              </td>
              <td className={classes.num} data-label={labels.price}>
                <span>
                  <span className={best ? `${classes.price} ${classes.bestPrice}` : classes.price}>{formatMoney(row.price)}</span>
                  {best ? <span className={classes.bestMark}>{t('app.bestPrice.results.best')}</span> : null}
                </span>
              </td>
              {withAction ? (
                <td className={classes.action}>
                  {row.to ? <BestPriceButton to={row.to} action={action} price={row.price} tone={best ? 'gain' : 'plain'} /> : null}
                </td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
